import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { User, Role } from 'app/auth/models';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  //public
  public currentUser: Observable<User>;

  //private
  private currentUserSubject: BehaviorSubject<User>;
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  /**
   *
   * @param {HttpClient} _http
   * @param {ToastrService} _toastrService
   */
  constructor(private _http: HttpClient, private _toastrService: ToastrService) {
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();
  }

  // getter: currentUserValue
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Admin;
  }

  /**
   *  Confirms if user is client
   */
  get isClient() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Client;
  }

  /**
   * User login
   *
   * @param email
   * @param password
   * @returns user
   */
  login(email: string, password: string) {
    return this._http
      .post<any>(`${environment.apiUrl}/api/dashboard/admin/login`, { email, password })
      .pipe(
        map(response => {
          // The dashboard API returns the account in `user` and the token at
          // the top level. Normalize it before storing currentUser so the
          // navbar can display the real account name after login and refresh.
          const user = response?.user
            ? { ...response.user, token: response.token }
            : response;

          // login successful if there's a jwt token in the response
          if (user && user.token) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', JSON.stringify(user));

            // Display welcome toast!
            setTimeout(() => {
              this._toastrService.success(
                  'You have successfully logged in. Now you can start to explore. Enjoy! 🎉',
                '👋 Welcome, ' + (user.name || user.firstName || '') + '!',
                { toastClass: 'toast ngx-toastr', closeButton: true }
              );
            }, 2500);

            // notify
            this.currentUserSubject.next(user);
          }

          return user;
        })
      );
  }

  setCurrentValue(next) {
    this.currentUserSubject.next(next);
  }
  /**
   * User logout
   *
   */
  logout() {
    const token = this.currentUserValue?.token;

    // Clear the local session immediately so the AuthGuard cannot keep the
    // user on a protected page while the server request is in flight.
    this.currentUserSubject.next(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userToken');
    sessionStorage.clear();

    this.isLoadingSubject.next(true);

    // HttpClient observables are lazy; the caller must subscribe for this
    // request to be sent. Treat an expired/failed server session as logged
    // out locally as well.
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    return this._http.post<any>(`${environment.apiUrl}/api/dashboard/logout`, {}, { headers }).pipe(
      catchError(() => of(null)),
      map(response => {
        this.isLoadingSubject.next(false);
        return response;
      })
    );
  }

}
